import { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2, Lock, Plus, Save, Trash2 } from 'lucide-react';
import { api } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}
interface PortalItem {
  portal: string;
  nome: string;
  custom: boolean;
  url: string | null;
  configurado: boolean;
  temUsuario: boolean;
  atualizadoEm: string | null;
}
interface ListData {
  cifraConfigurada: boolean;
  portais: PortalItem[];
}
interface EditState {
  usuario: string;
  senha: string;
  portalNome: string;
  portalUrl: string;
}

const vazio: EditState = { usuario: '', senha: '', portalNome: '', portalUrl: '' };

function formatarData(iso: string | null) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return null;
  }
}

export function EmpresaCredenciais({ empresaId }: { empresaId: string }) {
  const base = `/empresas/${empresaId}/credenciais`;
  const [data, setData] = useState<ListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  const [form, setForm] = useState<EditState>(vazio);
  const [salvando, setSalvando] = useState(false);
  const [revelado, setRevelado] = useState<Record<string, { usuario: string | null; senha: string }>>({});
  const [novo, setNovo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await api.get<ApiResponse<ListData>>(base);
      setData(body.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os acessos.');
    } finally {
      setLoading(false);
    }
  }, [base]);
  useEffect(() => {
    void load();
  }, [load]);

  const abrirEdicao = (item: PortalItem) => {
    setNovo(false);
    setEditando(item.portal);
    setForm({ ...vazio, portalNome: item.custom ? item.nome : '', portalUrl: item.url || '' });
    setNotice(null);
  };

  const salvar = async (item: PortalItem | null) => {
    setSalvando(true);
    setError(null);
    setNotice(null);
    try {
      const chave = item ? item.portal : 'outro';
      const payload = item?.custom === false
        ? { usuario: form.usuario, senha: form.senha }
        : { usuario: form.usuario, senha: form.senha, portalNome: form.portalNome, portalUrl: form.portalUrl };
      await api.put(`${base}/${encodeURIComponent(chave)}`, payload);
      setEditando(null);
      setNovo(false);
      setForm(vazio);
      setNotice('Acesso salvo com segurança.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar o acesso.');
    } finally {
      setSalvando(false);
    }
  };

  const revelar = async (item: PortalItem) => {
    if (revelado[item.portal]) {
      setRevelado((old) => {
        const copia = { ...old };
        delete copia[item.portal];
        return copia;
      });
      return;
    }
    try {
      const body = await api.post<ApiResponse<{ usuario: string | null; senha: string }>>(
        `${base}/${encodeURIComponent(item.portal)}/revelar`
      );
      setRevelado((old) => ({ ...old, [item.portal]: body.data }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível revelar o acesso.');
    }
  };

  const remover = async (item: PortalItem) => {
    setError(null);
    try {
      await api.delete(`${base}/${encodeURIComponent(item.portal)}`);
      setNotice('Acesso removido.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível remover o acesso.');
    }
  };

  const campo = (label: string, key: keyof EditState, type = 'text') => (
    <label className="block text-sm">
      <span className="mb-1.5 block font-medium">{label}</span>
      <input
        type={type}
        value={form[key]}
        onChange={(event) => setForm((old) => ({ ...old, [key]: event.target.value }))}
        className="w-full rounded-lg border border-ds-border bg-ds-surface px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ds-brand"
        autoComplete="off"
      />
    </label>
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-ds-border bg-ds-surface2/50 pb-4">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-ds-brand" />
          <CardTitle className="text-base">Acessos aos portais</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        <p className="text-sm text-ds-text-secondary">
          Login e senha dos portais desta empresa. As senhas são guardadas cifradas e usadas pela
          plataforma para operar em nome da empresa (robô de lances e monitoramento).
        </p>

        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-ds-text-secondary">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando acessos…
          </div>
        ) : (
          <>
            {data && !data.cifraConfigurada && (
              <div role="alert" className="mt-4 flex items-center gap-2 rounded-lg border border-ds-warning/30 bg-ds-warning/10 p-3 text-sm">
                <Lock className="h-4 w-4" /> O cofre não está habilitado no servidor (chave de criptografia ausente).
              </div>
            )}
            {error && <div role="alert" className="mt-4 rounded-lg bg-ds-danger/10 p-3 text-sm text-ds-danger">{error}</div>}
            {notice && <div role="status" className="mt-4 rounded-lg bg-ds-success/10 p-3 text-sm text-ds-success">{notice}</div>}

            <ul className="mt-4 divide-y divide-ds-border">
              {data?.portais.map((item) => (
                <li key={item.portal} className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-ds-text-primary">{item.nome}</p>
                      <p className="text-sm text-ds-text-secondary">
                        {item.configurado ? (
                          <>•••••••• {item.atualizadoEm && `· atualizado em ${formatarData(item.atualizadoEm)}`}</>
                        ) : (
                          'Nenhum acesso salvo'
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {item.configurado && (
                        <Button variant="ghost" size="sm" onClick={() => void revelar(item)} aria-label={revelado[item.portal] ? 'Ocultar' : 'Revelar'}>
                          {revelado[item.portal] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => abrirEdicao(item)}>
                        {item.configurado ? 'Editar' : 'Adicionar'}
                      </Button>
                      {item.configurado && (
                        <Button variant="ghost" size="sm" onClick={() => void remover(item)} aria-label="Remover acesso">
                          <Trash2 className="h-4 w-4 text-ds-danger" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {revelado[item.portal] && (
                    <div className="mt-2 rounded-lg bg-ds-surface2 p-3 text-sm">
                      <p><span className="text-ds-text-secondary">Usuário:</span> {revelado[item.portal].usuario || '—'}</p>
                      <p><span className="text-ds-text-secondary">Senha:</span> <code>{revelado[item.portal].senha}</code></p>
                    </div>
                  )}

                  {editando === item.portal && (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {item.custom && campo('Nome do portal', 'portalNome')}
                      {item.custom && campo('URL', 'portalUrl')}
                      {campo('Usuário', 'usuario')}
                      {campo('Senha', 'senha', 'password')}
                      <div className="sm:col-span-2 flex gap-2">
                        <Button onClick={() => void salvar(item)} disabled={salvando}>
                          <Save className="mr-2 h-4 w-4" />
                          {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                        <button className="px-3 text-sm text-ds-text-secondary" onClick={() => setEditando(null)}>Cancelar</button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-4">
              {novo ? (
                <div className="grid gap-3 rounded-lg border border-ds-border p-3 sm:grid-cols-2">
                  {campo('Nome do portal', 'portalNome')}
                  {campo('URL', 'portalUrl')}
                  {campo('Usuário', 'usuario')}
                  {campo('Senha', 'senha', 'password')}
                  <div className="sm:col-span-2 flex gap-2">
                    <Button onClick={() => void salvar(null)} disabled={salvando}>
                      <Save className="mr-2 h-4 w-4" />
                      {salvando ? 'Salvando…' : 'Salvar portal'}
                    </Button>
                    <button className="px-3 text-sm text-ds-text-secondary" onClick={() => { setNovo(false); setForm(vazio); }}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => { setNovo(true); setEditando(null); setForm(vazio); }}>
                  <Plus className="mr-2 h-4 w-4" /> Adicionar outro portal
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
